import { ref, toRaw } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { fabric } from 'fabric';
import config from '@/etc/rosepetal.json';

import { helper as $helper } from "@/utils/helper";
import * as rosepetalModel from "rosepetal-model";

import { canvas, layout } from '@/fabric/global.js';
import { createLabel, createPath } from '@/fabric/objects.js';
import { getCanvasId } from '@/fabric/events/segmentation.js';
import { resetSAMTimeout } from '@/fabric/events/sam.js'; 

const $vlm = rosepetalModel.default.vlm;
const $sam = rosepetalModel.default.sam;

// --- Estado Reactivo Exportado ---
export const VLMActive = ref(false);
export const vlmProcessing = ref(false);
export const autoNewImage = ref(false);
export const stepTimes = ref({
    instancePrepared: 0, instancePreparedStatus: '',
    instanceLoaded: 0, instanceLoadedStatus: '',
    modelReady: 0, modelReadyStatus: '',
    samPrepared: 0, samPreparedStatus: ''
});

// --- Estado Interno ---
let VLMtimeoutId = null;
const IOU_THRESHOLD = 0.95;

// --- Funciones Internas ---
function computeIOU(box1, box2) {
    const x1 = box1.left, y1 = box1.top, x1b = box1.left + box1.width, y1b = box1.top + box1.height;
    const x2 = box2.left, y2 = box2.top, x2b = box2.left + box2.width, y2b = box2.top + box2.height;
    const interLeft = Math.max(x1, x2), interTop = Math.max(y1, y2);
    const interRight = Math.min(x1b, x2b), interBottom = Math.min(y1b, y2b);

    if (interRight < interLeft || interBottom < interTop) return 0;
    
    const intersectionArea = (interRight - interLeft) * (interBottom - interTop);
    const area1 = box1.width * box1.height;
    const area2 = box2.width * box2.height;
    const unionArea = area1 + area2 - intersectionArea;
    return intersectionArea / unionArea;
}

// --- Funciones Públicas Exportadas ---

export function activateVLM() {
    VLMActive.value = true;
    console.log('VLM activated');
    if (VLMtimeoutId) clearTimeout(VLMtimeoutId);
    VLMtimeoutId = setTimeout(() => {
        VLMActive.value = false;
        console.log('VLM deactivated');
        VLMtimeoutId = null;
    }, 30000); // 30 segundos
}

export function deactivateVLM() {
    VLMActive.value = false;
    console.log('VLM deactivated');
    if (VLMtimeoutId) {
        clearTimeout(VLMtimeoutId);
        VLMtimeoutId = null;
    }
}

export async function autoDetectionVLM(imageUri) {
    autoNewImage.value = false;
    if (VLMActive.value) {
        try {
            await $vlm.setImage(imageUri, config.firebaseConfig.projectId);
            autoNewImage.value = true;
        } catch (error) {
            console.error('Error setting image for VLM:', error);
        }
    }
}

export function cleanupVLM() {
    if (VLMtimeoutId) {
        clearTimeout(VLMtimeoutId);
        VLMtimeoutId = null;
    }
}

export const handleVLMInference = async (data) => {
    vlmProcessing.value = true;
    stepTimes.value = { /* Reset steps */ };

    let t0, t1;
    if (!VLMActive.value) {
        try {
            
            t0 = Date.now();
            const orchResult = await $vlm.checkOrchestratorHealth(config.firebaseConfig.projectId);
            t1 = Date.now();
            stepTimes.value.instancePrepared = t1 - t0;
            stepTimes.value.instancePreparedStatus = orchResult.status || 'unknown';

            if (layout.value.dataset.type === 'imageObjectDetection') {
                t0 = Date.now();
                const florResult = await $vlm.checkFlorence2health(config.firebaseConfig.projectId);
                t1 = Date.now();
                stepTimes.value.instanceLoaded = t1 - t0;
                stepTimes.value.instanceLoadedStatus = florResult.status || 'unknown';
            } else if (layout.value.dataset.type === 'MULTILABEL') {
                const t0Flor = Date.now();
                const florPromise = $vlm.checkFlorence2health(config.firebaseConfig.projectId).then(res => {
                    const t1Flor = Date.now();
                    stepTimes.value.instanceLoaded = t1Flor - t0Flor;
                    stepTimes.value.instanceLoadedStatus = res.status || 'unknown';
                    return res;
                });

                const t0Sam = Date.now();
                const samPromise = $sam.checkSAMhealth(config.firebaseConfig.projectId).then(res => {
                    const t1Sam = Date.now();
                    stepTimes.value.samPrepared = t1Sam - t0Sam;
                    stepTimes.value.samPreparedStatus = res.status || 'unknown';
                    return res;
                });

                await Promise.all([florPromise, samPromise]);
            }
        } catch (error) {
            console.error("Health check failed", error);
            vlmProcessing.value = false;
            return;
        }
    }
    
    try {
        const vlm_option = data.vlmOptions;
        const promptList = data.prompts.map(item => item.prompt);
        const tagList = data.prompts.map(item => item.tag);

        for (let i = 0; i < promptList.length; i++) {
            const prompt = promptList[i];
            const tag = tagList[i];
            let prediction;

            if (i === 0) {
                t0 = Date.now();
                prediction = await $vlm.predict(vlm_option, prompt, layout.value.action.edit.uri, config.firebaseConfig.projectId);
                t1 = Date.now();
                stepTimes.value.modelReady = t1 - t0;
                stepTimes.value.modelReadyStatus = 'Prepared!';
                await new Promise(resolve => setTimeout(resolve, 2000));
                activateVLM();
            } else {
                prediction = await $vlm.predict(vlm_option, prompt, layout.value.action.edit.uri, config.firebaseConfig.projectId);
            }

            for (const item of prediction["results"]) {
                const [x, y, width, height] = item;
                if (layout.value.dataset.type === "MULTILABEL") {
                    const result = await $sam.predict(layout.value.action.edit.uri, config.firebaseConfig.projectId, 'segmentation', [], [x, y, x + width, y + height]);
                    resetSAMTimeout(); 
                    const pathOpt = { name: tag.id, color: tag.color, opacity: layout.value.canvas.filters.opacity };
                    let path = createPath(result.path, pathOpt, { previousPoints: [], previousBB: [] });
                    path.elementType = 'mask';
                    path.id = tag.id;
                    path.uuid_parent = getCanvasId(tag.id);
                    canvas.add(path);
                } else {
                    const candidate = new fabric.Rect({
                        uuid: uuidv4(), elementType: 'bbRect', name: tag.name, id: tag.id,
                        color: tag.color, stroke: tag.color, fill: $helper.hexToRgb(tag.color, 0.5),
                        strokeUniform: true, strokeWidth: 2, labeled: 'manual',
                        height: height, width: width, top: y, left: x,
                    });

                    const existingBoxes = canvas.getObjects().filter(obj => obj.elementType === 'bbRect' && obj.id === tag.id);
                    let duplicateFound = existingBoxes.some(existingBox => computeIOU(candidate, existingBox) >= IOU_THRESHOLD);

                    if (!duplicateFound) {
                        canvas.add(candidate);
                    }
                }
            }
        }
        canvas.renderAll();
        canvas._pushHistory();
    } catch (error) {
        console.error('Error in handleVLMInference:', error);
    } finally {
        vlmProcessing.value = false;
    }
};